(() => {
  'use strict';


  const instances = new WeakMap();

  class NinjaLoadMore {
    constructor(pager) {
      this.pager = pager;
      this.sectionId = pager.dataset.sectionId;
      this.root = pager.closest('.collection-content, .search-content') || document;
      this.grid = this.root.querySelector('[data-ninja-product-grid]');
      this.button = pager.querySelector('[data-ninja-load-button]');
      this.progress = pager.querySelector('[data-ninja-load-progress]');
      this.status = pager.querySelector('[data-ninja-load-status]');
      this.currentPage = Number(pager.dataset.currentPage || 1);
      this.totalPages = Number(pager.dataset.totalPages || 1);
      this.totalItems = Number(pager.dataset.totalItems || 0);
      this.nextUrl = pager.dataset.nextUrl || (this.button && this.button.href) || '';
      this.busy = false;
      this.restorePromise = null;

      if (!this.grid || !this.sectionId) return;

      this.routeKey = this.getRouteKey(window.location.href);
      this.storageKey = 'ninja-load-more:' + this.routeKey;
      this.onClick = this.onClick.bind(this);
      this.onGridClick = this.onGridClick.bind(this);

      this.grid.addEventListener('click', this.onGridClick);

      this.restorePromise = this.restore();
    }

    getRouteKey(value) {
      const url = new URL(value, window.location.origin);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      return url.pathname + '?' + url.searchParams.toString();
    }

    getStoredState() {
      try {
        const value = sessionStorage.getItem(this.storageKey);
        return value ? JSON.parse(value) : null;
      } catch (error) {
        return null;
      }
    }

    saveState(extra = {}) {
      const previous = this.getStoredState() || {};
      const state = Object.assign(previous, {
        routeKey: this.routeKey,
        loadedThrough: this.currentPage,
        updatedAt: Date.now()
      }, extra);

      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (error) {}

      const historyState = Object.assign({}, history.state || {}, {
        ninjaLoadMore: state
      });
      history.replaceState(historyState, '', window.location.href);
      return state;
    }

    directItemCount() {
      return Array.from(this.grid.children).filter((element) =>
        element.classList && element.classList.contains('grid__item')
      ).length;
    }

    updateProgress() {
      if (!this.progress) return;
      const loaded = Math.min(this.directItemCount(), this.totalItems);
      this.progress.textContent = loaded + ' / ' + this.totalItems + ' ' + this.pager.dataset.itemsLabel;
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      if (!this.button) return;
      this.button.setAttribute('aria-disabled', isBusy ? 'true' : 'false');
      if (isBusy) {
        this.button.dataset.originalLabel = this.button.textContent.trim();
        this.button.textContent = this.pager.dataset.loadingLabel;
      } else {
        this.button.textContent = this.button.dataset.originalLabel || this.pager.dataset.retryLabel;
      }
    }

    pageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      return url;
    }

    async fetchPage(urlValue) {
      const url = new URL(urlValue, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) throw new Error('HTTP ' + response.status);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const grid = documentFragment.querySelector('[data-ninja-product-grid]');
      const pager = documentFragment.querySelector('[data-ninja-load-more]');

      if (!grid || !pager) throw new Error('Pagination markup missing');

      return {
        children: Array.from(grid.children),
        nextUrl: pager.dataset.nextUrl || '',
        currentPage: Number(pager.dataset.currentPage || 1)
      };
    }

    prepareImportedChild(child) {
      const imported = document.importNode(child, true);
      const animatedElements = [];

      if (imported.matches && imported.matches('[data-aos]')) {
        animatedElements.push(imported);
      }
      imported.querySelectorAll('[data-aos]').forEach((element) => animatedElements.push(element));

      animatedElements.forEach((element) => {
        element.classList.add('aos-init', 'aos-animate');
      });

      return imported;
    }

    importChildren(children) {
      const fragment = document.createDocumentFragment();
      children.forEach((child) => fragment.appendChild(this.prepareImportedChild(child)));
      return fragment;
    }

    async restore() {
      const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (navigation && navigation.type === 'back_forward' && this.currentPage === 1) {
        const stored = this.getStoredState();
        if (!stored || !stored.loadedThrough || stored.loadedThrough === 1) {
          this.restoreAnchor(stored);
          return;
        }
      }

      if (this.currentPage > 1) {
        this.setBusy(true);
        try {
          const requests = [];
          for (let page = 1; page < this.currentPage; page += 1) {
            requests.push(this.fetchPage(this.pageUrl(page)));
          }
          const pages = await Promise.all(requests);
          const fragment = document.createDocumentFragment();
          pages.forEach((page) => {
            page.children.forEach((child) => fragment.appendChild(this.prepareImportedChild(child)));
          });
          this.grid.prepend(fragment);
          this.grid.classList.add('pag-loaded');
        } catch (error) {
          this.showError();
          return;
        } finally {
          this.setBusy(false);
        }
      }

      this.updateProgress();
      this.restoreAnchor(this.getStoredState());
    }

    restoreAnchor(state) {
      if (!state || !state.anchorHref) return;

      const targetLink = Array.from(this.grid.querySelectorAll('a[href]')).find((link) =>
        link.href === state.anchorHref
      );
      const target = targetLink && targetLink.closest('.grid__item');
      if (!target) return;

      const offset = Number.isFinite(state.viewportOffset) ? state.viewportOffset : 120;
      const move = () => {
        const top = window.scrollY + target.getBoundingClientRect().top - offset;
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'auto' });
      };

      requestAnimationFrame(() => {
        move();
        setTimeout(move, 180);
      });
    }

    onGridClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || !this.grid.contains(link)) return;

      const item = link.closest('.grid__item');
      if (!item) return;

      this.saveState({
        anchorHref: link.href,
        viewportOffset: item.getBoundingClientRect().top
      });
    }

    async onClick(event) {
      event.preventDefault();
      if (this.busy || !this.nextUrl) return;

      this.setBusy(true);
      if (this.status) this.status.textContent = this.pager.dataset.loadingLabel;

      try {
        const page = await this.fetchPage(this.nextUrl);
        this.grid.append(this.importChildren(page.children));
        this.grid.classList.add('pag-loaded');
        this.currentPage = page.currentPage;
        this.nextUrl = page.nextUrl;

        const visibleUrl = this.pageUrl(this.currentPage);
        visibleUrl.searchParams.delete('section_id');

        const state = this.saveState({ anchorHref: null, viewportOffset: null });
        history.replaceState(
          Object.assign({}, history.state || {}, { ninjaLoadMore: state }),
          '',
          visibleUrl.pathname + visibleUrl.search + visibleUrl.hash
        );

        this.updateProgress();
        if (this.status) this.status.textContent = '';

        if (this.nextUrl) {
          this.button.href = this.nextUrl;
          this.pager.dataset.nextUrl = this.nextUrl;
        } else {
          this.button.remove();
          this.button = null;
        }
      } catch (error) {
        this.showError();
      } finally {
        this.setBusy(false);
      }
    }

    showError() {
      if (this.status) this.status.textContent = this.pager.dataset.errorLabel;
      if (this.button) {
        this.button.textContent = this.pager.dataset.retryLabel;
        this.button.dataset.originalLabel = this.pager.dataset.retryLabel;
      }
    }
  }

  function init(root = document) {
    const pagers = root.querySelectorAll('[data-ninja-load-more]');
    pagers.forEach((pager) => {
      if (instances.has(pager)) return;
      instances.set(pager, new NinjaLoadMore(pager));
    });
  }

  function boot() {
    init();

    document.addEventListener('shopify:section:load', (event) => init(event.target));

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ninja-load-button]');
      if (!button) return;
      const pager = button.closest('[data-ninja-load-more]');
      const instance = pager && instances.get(pager);
      if (instance) instance.onClick(event);
    }, true);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-ninja-load-more]')) init(node.parentElement || document);
          else if (node.querySelector && node.querySelector('[data-ninja-load-more]')) init(node);
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();